export const fontFamilies = {
  "font-family-default": "'IBM Plex Sans', sans-serif",
  "font-family-signature-01": "'La Belle Aurore', cursive",
  "font-family-signature-02": "'Reenie Beanie', cursive",
  "font-family-signature-03": "'Caveat', cursive",
  "font-family-signature-04": "'Mrs Saint Delafield', cursive",
} as const;

export type CascadiaFontFamilyTypes = typeof fontFamilies;
export type CascadiaFontFamily = keyof CascadiaFontFamilyTypes;

export const sizes = {
  "font-size-50": { px: 12, rem: 0.75 },
  "font-size-75": { px: 14, rem: 0.875 },
  "font-size-100": { px: 16, rem: 1 },
  "font-size-200": { px: 20, rem: 1.25 },
  "font-size-300": { px: 24, rem: 1.5 },
  "font-size-400": { px: 28, rem: 1.75 },
  "font-size-500": { px: 32, rem: 2 },
  "font-size-600": { px: 40, rem: 2.5 },
} as const;

export type CascadiaFontSizeTypes = typeof sizes;
export type CascadiaFontSize = keyof CascadiaFontSizeTypes;

export const lineHeights = {
  "font-line-height-1": { px: 16, rem: 1 },
  "font-line-height-2": { px: 20, rem: 1.25 },
  "font-line-height-3": { px: 24, rem: 1.5 },
  "font-line-height-4": { px: 28, rem: 1.7 },
  "font-line-height-5": { px: 32, rem: 2 },
  "font-line-height-6": { px: 40, rem: 2.5 },
  "font-line-height-7": { px: 48, rem: 3 },
} as const;

export type CascadiaFontLineHeightTypes = typeof lineHeights;
export type CascadiaFontLineHeight = keyof CascadiaFontLineHeightTypes;

export const weights = {
  "font-weight-regular": "400",
  "font-weight-medium": "500",
  "font-weight-semibold": "600",
  "font-weight-bold": "700",
} as const;

export type CascadiaFontWeightTypes = typeof weights;
export type CascadiaFontWeight = keyof CascadiaFontWeightTypes;

export const decorations = {
  "font-deco-underline": "underline",
  "font-deco-uppercase": "uppercase",
  "font-deco-none": "none",
};

export type CascadiaFontDecorationTypes = typeof decorations;
export type CascadiaFontDecoration = keyof CascadiaFontDecorationTypes;

export const textStyles = {
  "global/body-list": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/body-primary": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/body-primary-link": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-regular",
    decoration: "font-deco-underline",
    fontFamily: "font-family-default",
  },
  "global/body-secondary": {
    size: "font-size-75",
    lineHeight: "font-line-height-2",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/body-secondary-link": {
    size: "font-size-75",
    lineHeight: "font-line-height-2",
    weight: "font-weight-regular",
    decoration: "font-deco-underline",
    fontFamily: "font-family-default",
  },
  "global/dropdown-menu-primary": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/footer-small": {
    size: "font-size-50",
    lineHeight: "font-line-height-1",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/label-small": {
    size: "font-size-50",
    lineHeight: "font-line-height-1",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/modal-header": {
    size: "font-size-200",
    lineHeight: "font-line-height-3",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/modal-header-small": {
    size: "font-size-100",
    lineHeight: "font-line-height-2",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/title-large": {
    size: "font-size-500",
    lineHeight: "font-line-height-6",
    weight: "font-weight-semibold",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/title-medium": {
    size: "font-size-400",
    lineHeight: "font-line-height-6",
    weight: "font-weight-semibold",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/title-small": {
    size: "font-size-300",
    lineHeight: "font-line-height-5",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/heading-large": {
    size: "font-size-200",
    lineHeight: "font-line-height-5",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/heading-medium": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/heading-small": {
    size: "font-size-75",
    lineHeight: "font-line-height-2",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/toast": {
    size: "font-size-75",
    lineHeight: "font-line-height-2",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/info-label": {
    size: "font-size-50",
    lineHeight: "font-line-height-1",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/branding-large": {
    size: "font-size-300",
    lineHeight: "font-line-height-4",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },

  "input/label": {
    size: "font-size-75",
    lineHeight: "font-line-height-2",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "input/text-chip": {
    size: "font-size-75",
    lineHeight: "font-line-height-2",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "input/text-field": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "input/text-field-code": {
    size: "font-size-200",
    lineHeight: "font-line-height-5",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "input/select-group": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "input/helper": {
    size: "font-size-75",
    lineHeight: "font-line-height-2",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "input/inline-error": {
    size: "font-size-75",
    lineHeight: "font-line-height-2",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "input/section-title": {
    size: "font-size-50",
    lineHeight: "font-line-height-1",
    weight: "font-weight-regular",
    decoration: "font-deco-uppercase",
    fontFamily: "font-family-default",
  },
  "input/signature-01": {
    size: "font-size-400",
    lineHeight: "font-line-height-5",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-signature-01",
  },
  "input/signature-02": {
    size: "font-size-400",
    lineHeight: "font-line-height-5",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-signature-02",
  },
  "input/signature-03": {
    size: "font-size-400",
    lineHeight: "font-line-height-5",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-signature-03",
  },
  "input/signature-04": {
    size: "font-size-400",
    lineHeight: "font-line-height-5",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-signature-04",
  },

  "global/button-large": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/button-medium": {
    size: "font-size-75",
    lineHeight: "font-line-height-2",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/button-small": {
    size: "font-size-50",
    lineHeight: "font-line-height-1",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },

  "global/metric-xlarge": {
    size: "font-size-500",
    lineHeight: "font-line-height-6",
    weight: "font-weight-semibold",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/metric-large": {
    size: "font-size-400",
    lineHeight: "font-line-height-6",
    weight: "font-weight-semibold",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/metric-medium": {
    size: "font-size-300",
    lineHeight: "font-line-height-6",
    weight: "font-weight-semibold",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/metric-small": {
    size: "font-size-200",
    lineHeight: "font-line-height-4",
    weight: "font-weight-semibold",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/metric-label": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/metric-label-small": {
    size: "font-size-50",
    lineHeight: "font-line-height-1",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "global/metric-detail": {
    size: "font-size-50",
    lineHeight: "font-line-height-1",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },

  "form/page-title": {
    size: "font-size-300",
    lineHeight: "font-line-height-5",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "form/page-title-mobile": {
    size: "font-size-200",
    lineHeight: "font-line-height-4",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "form/section-heading": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-semibold",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "form/body-primary": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "form/body-primary-link": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-regular",
    decoration: "font-deco-underline",
    fontFamily: "font-family-default",
  },
  "form/body-secondary": {
    size: "font-size-75",
    lineHeight: "font-line-height-2",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "form/body-secondary-link": {
    size: "font-size-75",
    lineHeight: "font-line-height-2",
    weight: "font-weight-regular",
    decoration: "font-deco-underline",
    fontFamily: "font-family-default",
  },
  "form/list": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "form/list-secondary": {
    size: "font-size-75",
    lineHeight: "font-line-height-2",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },

  "summary/group-header": {
    size: "font-size-200",
    lineHeight: "font-line-height-3",
    weight: "font-weight-semibold",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "summary/group-edit": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-medium",
    decoration: "font-deco-underline",
    fontFamily: "font-family-default",
  },
  "summary/content-primary": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "summary/content-secondary": {
    size: "font-size-75",
    lineHeight: "font-line-height-2",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "summary/section-title": {
    size: "font-size-50",
    lineHeight: "font-line-height-1",
    weight: "font-weight-regular",
    decoration: "font-deco-uppercase",
    fontFamily: "font-family-default",
  },
  "summary/legal-primary": {
    size: "font-size-75",
    lineHeight: "font-line-height-2",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "summary/nav-list-mobile": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "summary/nav-list": {
    size: "font-size-75",
    lineHeight: "font-line-height-2",
    weight: "font-weight-regular",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },

  "card/title-large": {
    size: "font-size-200",
    lineHeight: "font-line-height-4",
    weight: "font-weight-semibold",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "card/title-medium": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-medium",
    decoration: "font-deco-none",
    fontFamily: "font-family-default",
  },
  "pdf/row-header": {
    size: "font-size-100",
    lineHeight: "font-line-height-3",
    weight: "font-weight-medium",
    decoration: "font-deco-underline",
    fontFamily: "font-family-default",
  },
} as const;

export type CascadiaTypographyStyleTypes = typeof textStyles;
export type CascadiaTypographyStyle = keyof CascadiaTypographyStyleTypes;
